import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    tipo_cliente: '',
    stato: '',
    page: 1,
    limit: 10
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Debug all'inizio del componente
  useEffect(() => {
// console.log('ClientList montato');
  }, []);

  useEffect(() => {
    loadClients();
  }, [filters, sortBy, sortOrder]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
// console.log('Richiesta getClients con parametri:', params);
      let data;
      
      try {
        const response = await api.get("/clienti", { params });
        data = response.data;
// console.log('Dati ricevuti da getClients:', data);
      } catch (apiError) {
        console.error('Errore nella chiamata API:', apiError);
        // Failsafe mock data in caso di errore
        data = {
          clients: [],
          total: 0,
          totalPages: 1,
          page: 1
        };
      }
      
      // Garantiamo che clients sia sempre un array
      const safeClients = Array.isArray(data?.clients) ? data.clients : [];
      
      // Gestione sicura dei dati
      setClients(safeClients);
      setTotalPages(data?.totalPages || 1);
      setTotalClients(data?.total || 0);
      
      if (safeClients.length === 0 && !error) {
        setError('Nessun cliente trovato');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Errore nel caricamento dei clienti:', err);
      setClients([]);
      setTotalPages(1);
      setTotalClients(0);
      setError(err.error || err.message || 'Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to page 1 when filter changes
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadClients();
  };

  const handleSort = (column) => {
    if (column === sortBy) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFilters(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortBy) return null;
    return sortOrder === 'ASC' 
      ? <ArrowUpIcon className="h-4 w-4 inline-block ml-1 text-blue-600" /> 
      : <ArrowDownIcon className="h-4 w-4 inline-block ml-1 text-blue-600" />;
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      tipo_cliente: '',
      stato: '',
      page: 1,
      limit: 10
    });
  };

  return (
    <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-800">Lista Clienti</h2>
        <Link 
          to="/nlt/clienti/nuovo" 
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md flex items-center justify-center transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          <span>Nuovo Cliente</span>
        </Link>
      </div>

      {/* Filtri responsive */}
      <div className="bg-white p-4 sm:p-6 mb-6 sm:mb-8 rounded-lg shadow-sm border border-gray-100">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Riga ricerca */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="search"
              placeholder="Cerca per nome, ragione sociale, CF o P.IVA"
              value={filters.search}
              onChange={handleFilterChange}
              className="w-full py-2.5 pl-10 pr-4 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
            />
          </div>
          
          {/* Filtri in griglia responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Cliente</label>
              <select
                name="tipo_cliente"
                value={filters.tipo_cliente}
                onChange={handleFilterChange}
                className="w-full py-2.5 px-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              >
                <option value="">Tutti i tipi</option>
                <option value="privato">Privato</option>
                <option value="azienda">Azienda</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <select
                name="stato"
                value={filters.stato}
                onChange={handleFilterChange}
                className="w-full py-2.5 px-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              >
                <option value="">Tutti gli stati</option>
                <option value="attivo">Attivo</option>
                <option value="inattivo">Inattivo</option>
                <option value="sospeso">Sospeso</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Risultati per pagina</label>
              <select
                name="limit"
                value={filters.limit}
                onChange={handleFilterChange}
                className="w-full py-2.5 px-3 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              >
                <option value="10">10 per pagina</option>
                <option value="25">25 per pagina</option>
                <option value="50">50 per pagina</option>
              </select>
            </div>
          </div>

          {/* Pulsanti azioni */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
            >
              Cerca
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              Pulisci filtri
            </button>
          </div>
        </form>
      </div>

      {/* Contenuto principale */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 text-center">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Caricamento clienti...</span>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6 text-center">
          <div className="text-red-600 mb-2 font-medium">Errore</div>
          <div className="text-gray-600">{error}</div>
          <button
            onClick={loadClients}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Riprova
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          {/* Header risultati */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-sm text-gray-600">
                Trovati <span className="font-medium text-gray-900">{totalClients}</span> clienti
                {filters.search && (
                  <span> per <span className="font-medium">"{filters.search}"</span></span>
                )}
              </div>
              <div className="text-sm text-gray-500">
                Pagina {filters.page} di {totalPages}
              </div>
            </div>
          </div>

          {/* Lista clienti */}
          {clients.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-2">Nessun cliente trovato</div>
              <div className="text-sm text-gray-400">
                Prova a modificare i filtri di ricerca
              </div>
            </div>
          ) : (
            <>
              {/* Vista mobile - Cards */}
              <div className="block lg:hidden">
                <div className="divide-y divide-gray-200">
                  {clients.map((client) => (
                    <div key={client.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-base font-medium text-gray-900 truncate">
                              {client.tipo_cliente === 'azienda' 
                                ? client.ragione_sociale 
                                : `${client.nome} ${client.cognome}`
                              }
                            </h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              client.tipo_cliente === 'azienda' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {client.tipo_cliente === 'azienda' ? 'Azienda' : 'Privato'}
                            </span>
                          </div>
                          
                          <div className="space-y-1 text-sm text-gray-600">
                            {client.email && (
                              <div className="truncate">📧 {client.email}</div>
                            )}
                            {client.telefono && (
                              <div>📞 {client.telefono}</div>
                            )}
                            {client.codice_fiscale && (
                              <div className="truncate">
                                CF: {client.codice_fiscale}
                              </div>
                            )}
                            {client.partita_iva && (
                              <div className="truncate">
                                P.IVA: {client.partita_iva}
                              </div>
                            )}
                          </div>

                          {/* Responsabile */}
                          {client.responsabile && (
                            <div className="mt-2 text-sm text-gray-600">
                              👤 Responsabile: {client.responsabile.nome} {client.responsabile.cognome}
                            </div>
                          )}

                          {(client.indirizzo || client.citta) && (
                            <div className="mt-2 text-sm text-gray-500 truncate">
                              📍 {client.indirizzo} {client.citta}
                            </div>
                          )}
                        </div>
                        
                        <Link
                          to={`/clienti/${client.id}`}
                          className="ml-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-1.5 px-3 rounded-md transition-colors"
                        >
                          Dettagli
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vista desktop - Tabella */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('nome')}
                      >
                        <div className="flex items-center">
                          Nome/Ragione Sociale
                          {renderSortIcon('nome')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('tipo_cliente')}
                      >
                        <div className="flex items-center">
                          Tipo
                          {renderSortIcon('tipo_cliente')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contatti
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CF/P.IVA
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Responsabile
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center">
                          Data Creazione
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {client.tipo_cliente === 'azienda' 
                              ? client.ragione_sociale 
                              : `${client.nome} ${client.cognome}`
                            }
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {client.indirizzo} {client.citta}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            client.tipo_cliente === 'azienda' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {client.tipo_cliente === 'azienda' ? 'Azienda' : 'Privato'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="space-y-1">
                            {client.email && <div className="truncate max-w-xs">{client.email}</div>}
                            {client.telefono && <div>{client.telefono}</div>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="space-y-1">
                            {client.codice_fiscale && (
                              <div className="font-mono text-xs">CF: {client.codice_fiscale}</div>
                            )}
                            {client.partita_iva && (
                              <div className="font-mono text-xs">P.IVA: {client.partita_iva}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client.responsabile ? (
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <span className="text-sm font-medium text-blue-800">
                                    {client.responsabile.nome?.charAt(0)}{client.responsabile.cognome?.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {client.responsabile.nome} {client.responsabile.cognome}
                                </div>
                                <div className="text-sm text-gray-500">
                                  @{client.responsabile.username}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Non assegnato</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {client.createdAt ? new Date(client.createdAt).toLocaleDateString('it-IT') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/nlt/clienti/${client.id}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                          >
                            Visualizza
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Visualizzando <span className="font-medium">{((filters.page - 1) * filters.limit) + 1}</span> - <span className="font-medium">{Math.min(filters.page * filters.limit, totalClients)}</span> di <span className="font-medium">{totalClients}</span> risultati
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(filters.page - 1)}
                    disabled={filters.page === 1}
                    className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  <div className="hidden sm:flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            filters.page === page 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <div className="sm:hidden text-sm text-gray-700">
                    Pagina {filters.page} di {totalPages}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(filters.page + 1)}
                    disabled={filters.page === totalPages}
                    className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientList; 